import { getCsrfToken, clearCsrfToken } from './csrf'
import { appFetch } from './http'
import { apiUrl } from './native'

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/**
 * A non-2xx (non-401) API response. Carries the parsed JSON body so callers can
 * read the backend `{error}` code from the SAME response — no second request.
 * `message` stays `HTTP <status>` for back-compat with callers that matched on it.
 */
export class ApiError extends Error {
  status: number
  body: unknown
  constructor(status: number, body: unknown) {
    super(`HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

let onUnauthorized: () => void = () => {}
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn
}

export async function api<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase()
  const headers = new Headers(opts.headers)
  headers.set('Accept', 'application/json')
  if (opts.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (UNSAFE.has(method)) headers.set('X-CSRFToken', await getCsrfToken())

  let res = await appFetch(apiUrl(path), { ...opts, method, headers, credentials: 'include' })

  // Stale/expired CSRF token → refetch once and retry the unsafe call. ONLY 419
  // (the dedicated csrf_invalid status): a real 403 is an authorization error
  // (e.g. forbidden_key, or login's email_unverified) and must surface as-is, not
  // trigger a wasteful re-POST of a non-idempotent mutation.
  if (UNSAFE.has(method) && res.status === 419) {
    headers.set('X-CSRFToken', await getCsrfToken(true))
    res = await appFetch(apiUrl(path), { ...opts, method, headers, credentials: 'include' })
  }
  if (res.status === 401) {
    // Sesja 11 (artboard 09): `invalid_credentials` is the server saying the CURRENT password
    // typed into a re-auth field (change password, change e-mail, delete account) is wrong —
    // the session is fine. Treating it as an expiry logged the person out for a typo and made
    // the „Obecne hasło jest nieprawidłowe” branch of Settings unreachable. Read the body
    // first; only a 401 WITHOUT that code is a session that ended.
    let body: unknown = null
    try { body = await res.json() } catch { /* non-JSON error body → null */ }
    if ((body as { error?: string } | null)?.error === 'invalid_credentials') throw new ApiError(401, body)
    clearCsrfToken()
    onUnauthorized()
    throw new Error('unauthorized')
  }
  if (!res.ok) {
    let body: unknown = null
    try { body = await res.json() } catch { /* non-JSON error body → null */ }
    throw new ApiError(res.status, body)
  }
  return res.status === 204 ? (null as T) : ((await res.json()) as T)
}
